'use client';

import { useEffect, useMemo, useState } from 'react';
import { getNetworkNameById } from '@/utils/networkNames';
import type { VerificationTemplateProps } from './types';

function resolveLogoSrc(raw: string | null | undefined) {
    const value = (raw ?? '').trim();
    if (!value) return null;
    if (/^(https?:\/\/|data:)/.test(value)) return value;
    if (value.startsWith('/')) {
        const apiBase = (process.env.NEXT_PUBLIC_API_BASE || '').replace(/\/+$/, '');
        return apiBase ? `${apiBase}${value}` : value;
    }
    return value;
}

function isHexColor(value: string) {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

function withFallback(value: string | null | undefined, fallback: string) {
    const trimmed = (value ?? '').trim();
    return trimmed && isHexColor(trimmed) ? trimmed : fallback;
}

const STATUS_COPY: Record<string, { icon: string; label: string; color: string; sentence: string }> = {
    valid: { icon: '✓', label: 'Verified', color: '#1F9D8C', sentence: 'has successfully completed' },
    revoked: { icon: '✕', label: 'Revoked', color: '#DC4C3E', sentence: 'was issued a certificate for' },
    expired: { icon: '!', label: 'Expired', color: '#D98A2B', sentence: 'completed, but this certificate has expired for' },
};

// Fixed, always-readable ink for the card — deliberately NOT driven by tenant.tintText.
// tintText is used elsewhere in this app (QR margin styling, dark-background templates) as
// "text color for a dark surface"; this card is always a light surface, so using tintText here
// caused white-on-white text whenever a tenant's tintText was set to white.
const CARD_INK = '#111827';

export default function TemplateDiploma({
    certificate,
    tenant,
    verificationCode,
}: VerificationTemplateProps) {
    const issuerDisplayName = tenant?.issuerName || tenant?.name || 'Sigalmark';
    const logoSrc = resolveLogoSrc(tenant?.logoUrl);

    const accent = withFallback(tenant?.tintElements, '#32D6D3');
    const pageWash = withFallback(tenant?.tintBackground, '#F8FAFC');

    const [issuedDisplay, setIssuedDisplay] = useState('');
    const [expiryDisplay, setExpiryDisplay] = useState('');

    useEffect(() => {
        setIssuedDisplay(
            new Date(certificate.issued).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
            }),
        );
        setExpiryDisplay(
            new Date(certificate.expiry).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
            }),
        );
    }, [certificate.issued, certificate.expiry]);

    const status = STATUS_COPY[certificate.status] ?? STATUS_COPY.valid;
    const networkName = useMemo(() => getNetworkNameById(certificate.networkId), [certificate.networkId]);

    return (
        <div
            className="min-h-screen w-full flex items-start sm:items-center justify-center px-3 py-8 sm:p-10"
            style={{ backgroundColor: pageWash }}
        >
            <div className="w-full max-w-2xl">
                {/* Card with corner bracket accents — the brand's framing motif, not a reproduction of the paper certificate.
                    Card surface and text colors are fixed (not tenant-driven) so they always read clearly;
                    only the accent (borders, badges) follows the tenant's brand color. */}
                <div
                    className="relative rounded-2xl border bg-white px-5 py-10 sm:px-12 sm:py-14 text-center"
                    style={{ borderColor: `${accent}55`, color: CARD_INK }}
                >
                    {[
                        'top-2 left-2 border-t-2 border-l-2 rounded-tl-lg',
                        'top-2 right-2 border-t-2 border-r-2 rounded-tr-lg',
                        'bottom-2 left-2 border-b-2 border-l-2 rounded-bl-lg',
                        'bottom-2 right-2 border-b-2 border-r-2 rounded-br-lg',
                    ].map((cls) => (
                        <span
                            key={cls}
                            className={`pointer-events-none absolute ${cls} w-4 h-4 sm:w-6 sm:h-6`}
                            style={{ borderColor: accent }}
                        />
                    ))}

                    {/* Logo */}
                    <div className="flex items-center justify-center mb-6 sm:mb-8">
                        {logoSrc ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={logoSrc} alt={`${issuerDisplayName} logo`} className="h-9 sm:h-11 w-auto" />
                        ) : (
                            <span className="text-lg sm:text-xl font-semibold" style={{ color: CARD_INK }}>
                                {issuerDisplayName}
                            </span>
                        )}
                    </div>

                    {/* Status pill */}
                    <div className="flex justify-center mb-6">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs sm:text-sm font-semibold uppercase tracking-wide"
                            style={{ color: status.color, backgroundColor: `${status.color}1A` }}
                        >
                            <span>{status.icon}</span>
                            {status.label}
                        </span>
                    </div>

                    {/* Eyebrow */}
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <span className="h-px w-8 sm:w-12" style={{ backgroundColor: `${accent}88` }} />
                        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: accent }}>
                            Certificate Verification
                        </p>
                        <span className="h-px w-8 sm:w-12" style={{ backgroundColor: `${accent}88` }} />
                    </div>

                    {/* Name */}
                    <p className="text-3xl sm:text-4xl italic font-serif mb-4 break-words leading-tight" style={{ color: CARD_INK }}>
                        {certificate.name}
                    </p>

                    {/* Sentence + competency badge — the piece the Hero (Gradient) template doesn't need, but this one does */}
                    <p className="text-sm sm:text-base mb-3" style={{ color: `${CARD_INK}CC` }}>
                        {status.sentence}
                    </p>
                    <div className="flex justify-center mb-8">
                        <span
                            className="inline-block rounded-lg px-4 py-2 text-base sm:text-lg font-bold"
                            style={{ color: CARD_INK, backgroundColor: `${accent}22`, border: `1px solid ${accent}55` }}
                        >
                            {certificate.competency}
                        </span>
                    </div>

                    {/* Meta grid */}
                    <div className="grid grid-cols-2 gap-4 mb-8 text-sm text-left">
                        <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: `${accent}33` }}>
                            <p className="text-xs uppercase tracking-wide" style={{ color: `${CARD_INK}99` }}>
                                Issued
                            </p>
                            <p className="font-medium" style={{ color: CARD_INK }}>
                                {issuedDisplay || '—'}
                            </p>
                        </div>
                        <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: `${accent}33` }}>
                            <p className="text-xs uppercase tracking-wide" style={{ color: `${CARD_INK}99` }}>
                                Valid until
                            </p>
                            <p className="font-medium" style={{ color: CARD_INK }}>
                                {expiryDisplay || '—'}
                            </p>
                        </div>
                    </div>

                    {/* Verification details — no QR here, since this page IS the destination the QR/link points to */}
                    <div
                        className="rounded-xl p-4 sm:p-6 text-left space-y-1.5"
                        style={{ backgroundColor: `${accent}0D` }}
                    >
                        <p className="text-xs uppercase tracking-wide" style={{ color: `${CARD_INK}99` }}>
                            Verification number
                        </p>
                        <p className="text-sm font-mono break-all" style={{ color: CARD_INK }}>
                            {verificationCode}
                        </p>
                        <p className="text-xs break-all" style={{ color: `${CARD_INK}99` }}>
                            Contract: <span className="font-mono">{certificate.contract}</span> · Token #{certificate.tokenId} ·{' '}
                            {networkName}
                        </p>
                        {certificate.explorerUrl && (
                            <a
                                href={certificate.explorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block text-sm underline"
                                style={{ color: accent }}
                            >
                                View on blockchain explorer
                            </a>
                        )}
                    </div>
                </div>

                <p className="text-center text-xs mt-4" style={{ color: `${CARD_INK}99` }}>
                    Issued by {issuerDisplayName} · Powered by{' '}
                    <a href="https://sigalmark.com" target="_blank" rel="noopener noreferrer" className="underline">
                        sigalmark™
                    </a>
                </p>
            </div>
        </div>
    );
}
